import { createRef, forwardRef, useState } from "react";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import SimpleSlideshow from "../SimpleSlideshow";
import BjelinButton from "../BjelinButton";
import ImageLoader from "../ImageLoader";
import placeholderImage from "../../assets/placeholder-product-image.svg";
import defaultPlaceholderImage from "../../assets/placeholder-product-image.svg";
import { useProductUrl } from "../../utils/ProductUtils";
import FormattedPrice from "../FormattedPrice";
import IconsOrTagline from "../IconsOrTagline";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import Icon from "../Icon";
import StockStatus from "../StockStatus";
import { Flags } from "../Flags";
import { Product } from "../../types/Product";
import { ProductCategory } from "../../types/ProductCategory";
import { addNoOverlayState } from "../../utils/UrlUtils";
import {
  useProductCartHandler,
  useProductPrice,
  useProductSpecsExtended,
  useProductStockStatus
} from "../../hooks/ProductHooks";
import "./style.scss";

const motionVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  },
  out: {
    opacity: 0,
    scale: 0.8
  }
};

const ProductPreview = ({
  product,
  preferredCategory,
  layout = "card",
  showCartButton = false,
  dontExpandCart = false,
  noSlideShow = false,
  noOverlay = false,
  alwaysShowTagline = false,
  pkey,
  b2b = false
}: {
  product: Product;
  preferredCategory?: ProductCategory;
  layout?: "card" | "list";
  showCartButton?: boolean;
  dontExpandCart?: boolean;
  noSlideShow?: boolean;
  noOverlay?: boolean;
  alwaysShowTagline?: boolean;
  pkey: string;
  clickToFilter?: boolean;
  b2b?: boolean;
}) => {
  const features = useSiteFeatures();
  const tp = useProductTranslations();
  const t = useBjelinTranslations();

  const {
    isPurchasable,
    hasPrice,
    unitPrice,
    priceUnit,
    isPerSquareMeter,
    hasPreviousPrice,
    previousPrice,
    currency,
    unitDisplayPrice,
    displayPrice
  } = useProductPrice(product, b2b);
  const stockStatus = useProductStockStatus(product, tp, b2b);
  const { iconSpecsIndexed } = useProductSpecsExtended(product);
  const { inCart, addToCartHandler } = useProductCartHandler(
    product,
    1,
    dontExpandCart,
    b2b
  );

  const [playing, _setPlaying] = useState(false);
  const setPlaying = (play: boolean) => () => _setPlaying(play);
  const to = useProductUrl(product, preferredCategory);

  const { name, tagline, images, sku } = product;

  return (
    <motion.div
      role="listitem"
      layout={"position"}
      key={pkey}
      data-sku={sku}
      className={classNames("product-preview", "layout-" + layout, {
        withCartButton: showCartButton
      })}
      onMouseEnter={setPlaying(true)}
      onMouseLeave={setPlaying(false)}
      onTouchStart={setPlaying(true)}
      onTouchEnd={setPlaying(false)}
      variants={motionVariants}
      initial="hidden"
      animate="visible"
      exit="out"
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
    >
      <Link to={to || ""} state={noOverlay ? addNoOverlayState() : {}}>
        <div className={"section1"}>
          <ImageLoader
            className={"productPreloadImage"}
            containerClassName={"productImageContainer"}
            imageUrl={images?.[0].styles?.preview || ""}
          >
            {noSlideShow ? (
              <>
                <div
                  className="productPreviewImage"
                  style={{
                    backgroundImage: `url("${
                      images?.[0].styles?.preview || ""
                    }")`
                  }}
                >
                  <Flags product={product} inline={false} />
                </div>
              </>
            ) : (
              <SimpleSlideshow
                className={"productPreviewImage"}
                images={images}
                playing={playing}
                rewindOnPause={true}
              >
                <Flags product={product} inline={false} />
              </SimpleSlideshow>
            )}
          </ImageLoader>
        </div>
        <div className={"section2"}>
          <h4>
            {b2b && <span className="text-pale">{sku} </span>}
            <span>{name}</span>
          </h4>
          {alwaysShowTagline && <div className="tagline">{tagline}</div>}
          {hasPrice && (
            <div className={"price"}>
              {isPerSquareMeter && !!unitPrice ? (
                <FormattedPrice
                  price={unitDisplayPrice}
                  price_unit={priceUnit}
                  currency={currency}
                />
              ) : (
                <FormattedPrice
                  price={displayPrice}
                  sales_unit={priceUnit}
                  currency={currency}
                />
              )}
            </div>
          )}
          {hasPreviousPrice && (
            <div className={"previous-unit-price"}>
              {tp("previous_unit_price")}
              <FormattedPrice
                price={Number(previousPrice)}
                currency={currency}
              />
            </div>
          )}
        </div>
        <div className={"section3"}>
          {showCartButton && isPurchasable ? (
            inCart && inCart.amount ? (
              <div
                style={{
                  padding: "15px",
                  fontWeight: 700
                }}
                title={t("cart.existing.product", { amount: inCart.amount })}
              >
                <Icon name="check" /> {inCart.amount}{" "}
                {tp(
                  isPerSquareMeter
                    ? "price_unit.per_package"
                    : "sales_unit.per_package"
                )}
              </div>
            ) : (
              <BjelinButton
                color="primary"
                block={false}
                lg
                icon="cart-plus"
                onClick={e => {
                  e.preventDefault();
                  addToCartHandler();
                }}
              >
                {tp("add_to_cart.short")}
              </BjelinButton>
            )
          ) : (
            <div className={"specs"}>
              <IconsOrTagline
                key="icons"
                specsIndexed={iconSpecsIndexed}
                showLabel={layout !== "card"}
                sku={sku}
                tagline={alwaysShowTagline ? "" : tagline}
              />
            </div>
          )}
          {features.shop() && (product.saleable || product.sampleSaleable) && (
            <StockStatus
              stockStatus={stockStatus}
              sku={sku}
              key="stock-status"
            />
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductPreview;

export const ProductPreviewPlaceholder = forwardRef<
  HTMLDivElement,
  { product: Product }
>(({ product }, ref) => {
  ref = ref || createRef();
  const { name, sku } = product;

  return (
    <div
      className="render-placeholder product-preview"
      data-sku={sku}
      ref={ref}
    >
      <div className={"section1"}>
        <div
          className="image-placeholder"
          style={{ backgroundImage: `url('${defaultPlaceholderImage}')` }}
        />
      </div>
      <div className={"section2"}>
        <h4>{name}</h4>
        <div className="price" />
      </div>
      <div className={"section3"}>
        <div className="tagline" />
      </div>
    </div>
  );
});
ProductPreviewPlaceholder.displayName = "ProductPreviewPlaceholder";

export function ProductPreviewLoadingPlaceholder({
  layout
}: {
  layout: "card" | "list";
}) {
  const style = {
    backgroundImage: "url(" + placeholderImage + ")"
  };

  const layoutClass = layout ? "layout-" + layout : "layout-card";

  return (
    <motion.div
      className={classNames("product-preview", "placeholder", layoutClass)}
      aria-busy
    >
      <div className={"section1"}>
        <div
          className="productPreviewImage"
          role="img"
          aria-label="Placeholder Product Image"
          style={style}
        />
      </div>
      <div className={"section2"}>
        <div className={"flags"} />
        <h4> </h4>
        <div className="price" />
      </div>
      <div className={"section3"}>
        <div className="tagline" />
      </div>
    </motion.div>
  );
}
